class RelatedTabs extends HTMLElement {
  constructor() {
    super();

    this.mobileBreakpoint = 990;
    this.mediaQueryList = window.matchMedia(`(max-width: ${this.mobileBreakpoint - 1}px)`);
    this.tabsListEl = null;           // .tabs__list
    this.desktopPanelsHostEl = null;  // .tabs__panels
    this.tabButtons = [];             // Array of [data-tab]
    this.panelById = new Map();       // Map [data-panel]>
    this.onRootClick = this.onRootClick.bind(this);
    this.onBreakpointChange = this.onBreakpointChange.bind(this);
  }

  connectedCallback() {
    this.tabsListEl = this.querySelector('.tabs__list');
    this.desktopPanelsHostEl = this.querySelector('.tabs__panels');

    if (!this.tabsListEl || !this.desktopPanelsHostEl) return;

    this.tabButtons = Array.from(this.tabsListEl.querySelectorAll('[data-tab]'));

    const panelElements = Array.from(this.querySelectorAll('[data-panel]'));
    this.panelById = new Map(panelElements.map((panelEl) => [panelEl.dataset.panel, panelEl]));
    this.ensureDesktopInvariant();
    this.addEventListener('click', this.onRootClick);
    this.mediaQueryList.addEventListener('change', this.onBreakpointChange);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.onRootClick);
    this.mediaQueryList.removeEventListener('change', this.onBreakpointChange);
  }

  onRootClick(event) {
    const clickedButton = event.target.closest('[data-tab]');
    if (!clickedButton || !this.contains(clickedButton)) return;

    const isMobile = this.mediaQueryList.matches;

    if (isMobile) {
      // Mobile: toggle the clicked tab; others close
      const targetId = clickedButton.dataset.tab;
      const targetPanel = this.panelById.get(targetId);
      const willOpen = clickedButton.getAttribute('aria-expanded') !== 'true';

      this.tabButtons.forEach((tabBtn) => {
        const isTarget = tabBtn === clickedButton;
        const panel = this.panelById.get(tabBtn.dataset.tab);
        const open = isTarget ? willOpen : false;

        tabBtn.classList.toggle('is-active', open);
        tabBtn.setAttribute('aria-selected', open ? 'true' : 'false');
        tabBtn.setAttribute('aria-expanded', open ? 'true' : 'false');
        if (panel) panel.hidden = !open;
      });

      if (targetPanel) this.movePanelUnderTab(targetPanel, clickedButton, this.tabsListEl);
    } else {
      // Desktop: switch active tab; do nothing if already active
      if (clickedButton.classList.contains('is-active')) return;

      const targetId = clickedButton.dataset.tab;

      this.tabButtons.forEach((tabBtn) => {
        const isActive = tabBtn === clickedButton;
        tabBtn.classList.toggle('is-active', isActive);
        tabBtn.setAttribute('aria-selected', isActive ? 'true' : 'false');
        tabBtn.setAttribute('aria-expanded', isActive ? 'true' : 'false');

        const panel = this.panelById.get(tabBtn.dataset.tab);
        if (panel) panel.hidden = !isActive;
      });

      // Ensure all panels are under the desktop
      this.tabButtons.forEach((tabBtn) => {
        const panel = this.panelById.get(tabBtn.dataset.tab);
        if (panel && panel.parentElement !== this.desktopPanelsHostEl) {
          this.desktopPanelsHostEl.appendChild(panel);
        }
      });
    }
  }

  onBreakpointChange() {
    const isMobile = this.mediaQueryList.matches;

    if (isMobile) {
      // On mobile: each expanded tab shows its panel directly under the tab button
      this.tabButtons.forEach((tabBtn) => {
        const panel = this.panelById.get(tabBtn.dataset.tab);
        if (!panel) return;
        const expanded = tabBtn.getAttribute('aria-expanded') === 'true';
        if (expanded) {
          this.movePanelUnderTab(panel, tabBtn, this.tabsListEl);
          panel.hidden = false;
        } else {
          panel.hidden = true;
        }
      });
    } else {
      // On desktop: enforce invariant and move panels back
      this.ensureDesktopInvariant();

      this.tabButtons.forEach((tabBtn) => {
        const panel = this.panelById.get(tabBtn.dataset.tab);
        if (!panel) return;
        if (panel.parentElement !== this.desktopPanelsHostEl) {
          this.desktopPanelsHostEl.appendChild(panel);
        }
        panel.hidden = tabBtn.classList.contains('is-active') ? false : true;
      });
    }
  }

  ensureDesktopInvariant() {
    let activeTab = this.tabButtons.find((t) => t.classList.contains('is-active'));
    if (!activeTab) activeTab = this.tabButtons[0];

    this.tabButtons.forEach((tabBtn) => {
      const isActive = tabBtn === activeTab;
      tabBtn.classList.toggle('is-active', isActive);
      tabBtn.setAttribute('aria-selected', isActive ? 'true' : 'false');
      tabBtn.setAttribute('aria-expanded', isActive ? 'true' : 'false');
    });

    const isMobile = this.mediaQueryList.matches;

    this.tabButtons.forEach((tabBtn) => {
      const panel = this.panelById.get(tabBtn.dataset.tab);
      if (!panel) return;

      if (isMobile) {
        const visible = tabBtn.classList.contains('is-active');
        panel.hidden = !visible;
        if (visible) this.movePanelUnderTab(panel, tabBtn, this.tabsListEl);
      } else {
        this.desktopPanelsHostEl.appendChild(panel);
        panel.hidden = !tabBtn.classList.contains('is-active');
      }
    });
  }

  movePanelUnderTab(panelEl, tabButtonEl, listEl) {
    if (panelEl.previousElementSibling === tabButtonEl) return;
    listEl.insertBefore(panelEl, tabButtonEl.nextElementSibling);
  }
}

if (!customElements.get('related-tabs')) {
  customElements.define('related-tabs', RelatedTabs);
}
